<template>
  <div class="products-page">
    <!-- Background Elements -->
    <div class="page-grid"></div>
    <div class="page-orb orb-1"></div>
    <div class="page-orb orb-2"></div>

    <v-container class="products-container">
      <!-- Hero Section -->
      <div class="products-hero">
        <div class="hero-content">
          <div class="hero-icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M16 21V5C16 4.46957 15.7893 3.96086 15.4142 3.58579C15.0391 3.21071 14.5304 3 14 3H10C9.46957 3 8.96086 3.21071 8.58579 3.58579C8.21071 3.96086 8 4.46957 8 5V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div>
            <h1 class="hero-title">Products</h1>
            <p class="hero-subtitle">Explore your product workspaces</p>
          </div>
        </div>

        <div class="hero-search">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <input v-model="search" type="text" placeholder="Search products..." />
        </div>
      </div>

      <!-- Products Card -->
      <div class="products-card">
        <div class="card-filters">
          <div class="filter-chips">
            <span class="filter-label">Status:</span>
            <button
              v-for="s in statuses"
              :key="s"
              :class="['filter-chip', { active: statusFilter === s }]"
              @click="statusFilter = s"
            >
              <svg v-if="s !== 'all'" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path v-if="s === 'active'" d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path v-else-if="s === 'paused'" d="M5.33333 3.33333H6.66667V12.6667H5.33333V3.33333ZM9.33333 3.33333H10.6667V12.6667H9.33333V3.33333Z" fill="currentColor"/>
                <path v-else d="M13.3333 4V12C13.3333 12.3536 13.1929 12.6928 12.9428 12.9428C12.6928 13.1929 12.3536 13.3333 12 13.3333H4C3.64638 13.3333 3.30724 13.1929 3.05719 12.9428C2.80714 12.6928 2.66667 12.3536 2.66667 12V4M10.6667 4V3.33333C10.6667 2.97971 10.5262 2.64057 10.2761 2.39052C10.0261 2.14048 9.68696 2 9.33333 2H6.66667C6.31304 2 5.97391 2.14048 5.72386 2.39052C5.47381 2.64057 5.33333 2.97971 5.33333 3.33333V4M1.33333 4H14.6667" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ s }}</span>
            </button>
          </div>

          <div class="filter-sort">
            <select v-model="sortKey">
              <option value="updated-desc">Updated (newest)</option>
              <option value="updated-asc">Updated (oldest)</option>
              <option value="name-asc">Name (A → Z)</option>
              <option value="name-desc">Name (Z → A)</option>
            </select>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="products-loading">
          <div class="loader"></div>
          <p>Loading products...</p>
        </div>

        <!-- Products List -->
        <div v-else-if="sortedFiltered.length > 0" class="products-list">
          <div
            v-for="p in sortedFiltered"
            :key="p.id"
            class="product-item"
            @click="$router.push({ name: 'StaffProductDetail', params: { id: p.id } })"
          >
            <div class="product-avatar" :style="{ background: avatarGradient(p.name) }">
              {{ initials(p.name) }}
            </div>

            <div class="product-content">
              <div class="product-name">{{ p.name }}</div>
              <div class="product-summary">{{ p.summary }}</div>
            </div>

            <div class="product-meta">
              <div class="meta-label">Updated</div>
              <div class="meta-value">{{ prettyTs(p.updatedAt) }}</div>
            </div>

            <span :class="['product-status', 'status-' + (p.status || 'active').toLowerCase()]">
              {{ p.status }}
            </span>

            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" class="product-arrow">
              <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="products-empty">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <path d="M53.3333 18.6667H10.6667C8.08934 18.6667 6 20.756 6 23.3333V50.6667C6 53.244 8.08934 55.3333 10.6667 55.3333H53.3333C55.9107 55.3333 58 53.244 58 50.6667V23.3333C58 20.756 55.9107 18.6667 53.3333 18.6667Z" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M42.6667 55.3333V13.3333C42.6667 11.9188 42.1048 10.5623 41.1046 9.5621C40.1044 8.5619 38.7478 8 37.3333 8H26.6667C25.2522 8 23.8956 8.5619 22.8954 9.5621C21.8952 10.5623 21.3333 11.9188 21.3333 13.3333V55.3333" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <h3>No products match your filters</h3>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import { db } from '@/firebase'
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore'

export default {
  name: 'StaffProducts',
  data() {
    return {
      loading: true,
      products: [],
      unsub: null,
      search: '',
      statusFilter: 'all',
      statuses: ['all', 'active', 'paused', 'archived'],
      sortKey: 'updated-desc',
    }
  },
  created() {
    const qRef = query(collection(db, 'products'), orderBy('updatedAt', 'desc'))
    this.unsub = onSnapshot(qRef, snap => {
      this.products = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      this.loading = false
    }, () => {
      this.loading = false
    })
  },
  beforeDestroy() {
    if (this.unsub) this.unsub()
  },
  computed: {
    filtered() {
      const q = (this.search || '').toLowerCase()
      const s = (this.statusFilter || 'all').toLowerCase()
      return this.products
        .filter(p => s === 'all' ? true : (p.status || '').toLowerCase() === s)
        .filter(p => !q || [p.name, p.summary, p.slug].join(' ').toLowerCase().includes(q))
    },
    sortedFiltered() {
      const arr = [...this.filtered]
      const key = this.sortKey
      const toDate = (ts) => ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : null)
      if (key === 'updated-desc') return arr.sort((a, b) => (toDate(b.updatedAt) - toDate(a.updatedAt)))
      if (key === 'updated-asc') return arr.sort((a, b) => (toDate(a.updatedAt) - toDate(b.updatedAt)))
      if (key === 'name-asc') return arr.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
      if (key === 'name-desc') return arr.sort((a, b) => String(b.name || '').localeCompare(String(a.name || '')))
      return arr
    }
  },
  methods: {
    initials(name = '') {
      return name.split(/\s+/).map(x => x[0]).join('').slice(0, 2).toUpperCase()
    },
    avatarGradient(name = '') {
      const gradients = [
        'linear-gradient(135deg, #00fff5, #7b2ff7)',
        'linear-gradient(135deg, #7b2ff7, #ff006e)',
        'linear-gradient(135deg, #ff006e, #ffbe0b)',
        'linear-gradient(135deg, #ffbe0b, #00fff5)'
      ]
      const index = Math.abs(name.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % gradients.length
      return gradients[index]
    },
    toDate(ts) {
      if (!ts) return null
      if (ts.toDate) return ts.toDate()
      if (ts.seconds) return new Date(ts.seconds * 1000)
      return null
    },
    prettyTs(ts) {
      const d = this.toDate(ts)
      if (!d) return '—'
      return d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: 'numeric', minute: '2-digit' })
    }
  }
}
</script>

<style scoped>
  /* ==================== PAGE BACKGROUND ==================== */
  .products-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #1B3A5C 0%, #D4A253 100%);
    position: relative;
    overflow-x: hidden;
    font-family: 'Roboto', sans-serif;
    padding: 40px 0 80px;
  }
  
  .page-grid {
    position: fixed;
    inset: 0;
    background-image: 
      linear-gradient(to right, rgba(255, 255, 255, 0.08) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255, 255, 255, 0.08) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%);
    animation: grid-flow 35s linear infinite;
    pointer-events: none;
    z-index: 0;
  }
  
  @keyframes grid-flow {
    0% { transform: translate(0, 0); }
    100% { transform: translate(60px, 60px); }
  }
  
  .page-orb {
    position: fixed;
    border-radius: 50%;
    filter: blur(140px);
    opacity: 0.3;
    animation: orb-float 18s ease-in-out infinite;
    pointer-events: none;
    z-index: 0;
  }
  
  .orb-1 {
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(255,255,255,0.3), transparent);
    top: -200px;
    left: -200px;
  }
  
  .orb-2 {
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(212,162,83,0.3), transparent);
    bottom: -150px;
    right: -150px;
    animation-delay: 6s;
  }
  
  @keyframes orb-float {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(50px, -50px) scale(1.1); }
    66% { transform: translate(-40px, 40px) scale(0.9); }
  }
  
  .products-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
    position: relative;
    z-index: 2;
  }
  
  /* ==================== HERO ==================== */
  .products-hero {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 32px;
    flex-wrap: wrap;
  }
  
  .hero-content {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  
  .hero-icon {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #1B3A5C, #2C5282);
    box-shadow: 0 8px 30px rgba(27, 58, 92, 0.4);
    color: #ffffff;
  }
  
  .hero-title {
    font-size: clamp(28px, 4vw, 42px);
    font-weight: 900;
    color: #ffffff;
    margin-bottom: 4px;
    letter-spacing: -0.02em;
    text-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  }
  
  .hero-subtitle {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.95);
  }
  
  .hero-search {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.2);
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 12px;
    transition: all 0.3s ease;
    min-width: 320px;
    backdrop-filter: blur(10px);
  }
  
  .hero-search:focus-within {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  .hero-search svg {
    color: rgba(255, 255, 255, 0.9);
    flex-shrink: 0;
  }
  
  .hero-search input {
    flex: 1;
    background: transparent;
    border: none;
    color: #ffffff;
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    outline: none;
  }
  
  .hero-search input::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
  
  /* ==================== PRODUCTS CARD ==================== */
  .products-card {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 24px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }
  
  /* ==================== FILTERS ==================== */
  .card-filters {
    padding: 24px;
    border-bottom: 1px solid #e9ecef;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
    background: #f8f9fa;
  }
  
  .filter-chips {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  
  .filter-label {
    font-size: 13px;
    font-weight: 700;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .filter-chip {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: white;
    border: 2px solid #dee2e6;
    border-radius: 8px;
    color: #212529;
    font-size: 13px;
    font-weight: 600;
    text-transform: capitalize;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .filter-chip:hover {
    background: #f8f9fa;
    border-color: #1B3A5C;
  }
  
  .filter-chip.active {
    background: linear-gradient(135deg, #1B3A5C, #2C5282);
    border-color: #1B3A5C;
    color: #ffffff;
    box-shadow: 0 4px 15px rgba(27, 58, 92, 0.3);
  }
  
  .filter-sort select {
    padding: 10px 14px;
    background: white;
    border: 2px solid #dee2e6;
    border-radius: 8px;
    color: #212529;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    font-weight: 600;
    outline: none;
    cursor: pointer;
    min-width: 200px;
    transition: all 0.3s ease;
  }
  
  .filter-sort select:focus {
    border-color: #1B3A5C;
    box-shadow: 0 0 0 4px rgba(27, 58, 92, 0.1);
  }
  
  .filter-sort select option {
    background: white;
    color: #212529;
  }
  
  /* ==================== PRODUCTS LIST ==================== */
  .products-list {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .product-item {
    display: grid;
    grid-template-columns: 56px 1fr auto auto 24px;
    gap: 16px;
    align-items: center;
    padding: 20px;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .product-item:hover {
    background: white;
    border-color: #D4A253;
    transform: translateX(4px);
    box-shadow: 0 4px 20px rgba(212, 162, 83, 0.2);
  }
  
  .product-avatar {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 700;
    color: #ffffff;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }
  
  .product-content {
    flex: 1;
    min-width: 0;
  }
  
  .product-name {
    font-size: 16px;
    font-weight: 700;
    color: #212529;
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .product-summary {
    font-size: 14px;
    color: #6c757d;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .product-meta {
    text-align: right;
  }
  
  .meta-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6c757d;
    margin-bottom: 4px;
  }
  
  .meta-value {
    font-size: 13px;
    color: #212529;
  }
  
  .product-status {
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    text-transform: capitalize;
  }
  
  .status-active {
    background: rgba(34, 197, 94, 0.2);
    color: #22c55e;
    border: 1px solid rgba(34, 197, 94, 0.3);
  }
  
  .status-paused {
    background: rgba(212, 162, 83, 0.2);
    color: #D4A253;
    border: 1px solid rgba(212, 162, 83, 0.3);
  }
  
  .status-archived {
    background: rgba(156, 163, 175, 0.2);
    color: #6b7280;
    border: 1px solid rgba(156, 163, 175, 0.3);
  }
  
  .product-arrow {
    color: #6c757d;
    transition: all 0.2s ease;
  }
  
  .product-item:hover .product-arrow {
    color: #D4A253;
    transform: translateX(4px);
  }
  
  /* ==================== LOADING STATE ==================== */
  .products-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;
    text-align: center;
  }
  
  .loader {
    width: 50px;
    height: 50px;
    border: 4px solid #e9ecef;
    border-top-color: #1B3A5C;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 20px;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  .products-loading p {
    color: #6c757d;
    font-size: 16px;
  }
  
  /* ==================== EMPTY STATE ==================== */
  .products-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;
    text-align: center;
  }
  
  .products-empty svg {
    color: #dee2e6;
    margin-bottom: 20px;
  }
  
  .products-empty h3 {
    font-size: 20px;
    font-weight: 700;
    color: #212529;
    margin-bottom: 8px;
  }
  
  .products-empty p {
    color: #6c757d;
    font-size: 16px;
  }
  
  /* ==================== RESPONSIVE ==================== */
  @media (max-width: 960px) {
    .products-hero {
      flex-direction: column;
      align-items: flex-start;
    }
  
    .hero-search {
      width: 100%;
    }
  
    .card-filters {
      flex-direction: column;
      align-items: flex-start;
    }
  
    .filter-sort select {
      width: 100%;
    }
  
    .product-item {
      grid-template-columns: 48px 1fr;
      gap: 12px;
    }
  
    .product-meta,
    .product-status,
    .product-arrow {
      display: none;
    }
  }
  
  @media (max-width: 600px) {
    .products-page {
      padding: 20px 0 40px;
    }
  
    .products-container {
      padding: 0 16px;
    }
  }
</style>